import React, { memo } from 'react';
import { Home, Compass, Heart, User } from 'lucide-react';
import { motion } from 'motion/react';
import { useAuth } from '../contexts/AuthContext';
import { PROFILE_THEMES } from '../constants/themes';

interface BottomNavProps {
  activeTab: string;
  onNavigate: (tab: string) => void;
  /** Reservado: usado anteriormente por um botão central de scanner. Mantido na API por compatibilidade. */
  onOpenScanner?: () => void;
}

const BottomNavInner: React.FC<BottomNavProps> = ({ activeTab, onNavigate }) => {
  const { profile } = useAuth();
  const activeTheme =
    PROFILE_THEMES[(profile?.themeId as keyof typeof PROFILE_THEMES) || 'default'] ||
    PROFILE_THEMES.default;

  const NavItem = ({ id, icon, label }: { id: string; icon: React.ReactNode; label: string }) => {
    const isActive = activeTab === id;
    return (
      <button
        type="button"
        data-tour={`nav-${id}`}
        onClick={() => onNavigate(id)}
        aria-label={`Ir para ${label}`}
        aria-current={isActive ? 'page' : undefined}
        className={`relative flex w-[3.1rem] flex-col items-center gap-1.5 transition-colors sm:w-14 ${isActive ? 'text-white' : 'text-slate-400 hover:text-slate-300'}`}
      >
        {isActive && (
          <motion.div
            layoutId="nav-indicator"
            className={`absolute -top-3.5 h-1 w-8 rounded-b-full bg-gradient-to-r ${activeTheme.primaryButton} shadow-[0_0_8px_rgba(255,255,255,0.18)]`}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          />
        )}
        <div
          className={`${isActive ? 'scale-105 drop-shadow-[0_0_6px_rgba(255,255,255,0.16)]' : ''} transition-transform duration-300`}
          aria-hidden="true"
        >
          {icon}
        </div>
        <span className="w-full truncate text-center text-[11px] font-bold tracking-wide sm:text-[11px]">
          {label}
        </span>
      </button>
    );
  };

  return (
    <>
      {/* SVG filter defs pro Liquid Glass (refração via displacement map).
          Width/height=0 + absolute pra não afetar layout. Só Safari consome. */}
      <svg
        width="0"
        height="0"
        aria-hidden="true"
        style={{ position: 'absolute', pointerEvents: 'none' }}
      >
        <defs>
          <filter id="sixpass-liquid-glass" x="0%" y="0%" width="100%" height="100%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.012 0.018"
              numOctaves="2"
              seed="7"
              result="noise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="noise"
              scale="35"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>

      <nav
        aria-label="Navegação principal"
        className="liquid-glass liquid-glass-refract fixed bottom-0 left-1/2 z-40 flex w-full max-w-md -translate-x-1/2 items-center justify-between overflow-hidden rounded-t-3xl border-t border-white/15 px-4 pt-4 shadow-[0_-14px_38px_rgba(0,0,0,0.55)] sm:px-6"
        style={{ paddingBottom: 'calc(var(--safe-bottom, 0px) + 1rem)' }}
      >
        {/* Specular highlight — linha de luz fina no topo */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/45 to-transparent"
        />
        {/* Glow suave abaixo da linha specular — sensação de "luz batendo" */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-px h-10 bg-gradient-to-b from-white/[0.07] to-transparent"
        />
        {/* Indicador colorido do tema (preservado) */}
        <div
          className={`pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r ${activeTheme.progressGradient} opacity-40 mix-blend-screen`}
        />
        <NavItem id="home" icon={<Home size={22} />} label="Início" />
        <NavItem id="explore" icon={<Compass size={22} />} label="Explorar" />
        <NavItem id="favorites" icon={<Heart size={22} />} label="Salvos" />
        <NavItem id="profile" icon={<User size={22} />} label="Perfil" />
      </nav>
    </>
  );
};

export const BottomNav = memo(BottomNavInner);
BottomNav.displayName = 'BottomNav';
